import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  type Messaging,
  type MessagePayload,
} from 'firebase/messaging';
import { logger } from '../utils/logger';

// Firebase config from environment
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY as string,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID as string,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as string,
  appId: import.meta.env.VITE_FIREBASE_APP_ID as string,
};

let appInstance: FirebaseApp | null = null;
let messagingInstance: Messaging | null = null;

export const ensureFirebaseMessaging = async (): Promise<Messaging | null> => {
  try {
    const supported = await isSupported().catch(() => false);
    if (!supported) {
      logger.warn('FCM not supported in this browser');
      return null;
    }

    if (!appInstance) {
      appInstance = initializeApp(firebaseConfig);
      logger.log('Firebase app initialized');
    }

    if (!messagingInstance) {
      messagingInstance = getMessaging(appInstance);
      logger.log('Firebase messaging initialized');
    }

    return messagingInstance;
  } catch (error) {
    logger.error('Failed to initialize Firebase Messaging:', error);
    return null;
  }
};

export const fetchFcmToken = async (
  swRegistration?: ServiceWorkerRegistration,
): Promise<string | null> => {
  const vapidKey = (import.meta.env.VITE_FCM_VAPID_KEY as string) || '';
  try {
    const messaging = await ensureFirebaseMessaging();
    if (!messaging) {
      return null;
    }
    if (!vapidKey) {
      logger.warn('Missing VITE_FCM_VAPID_KEY environment value');
      return null;
    }

    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: swRegistration,
    });

    if (token) {
      logger.log('FCM token obtained');
      return token;
    }
    logger.warn('FCM token was null/empty');
    return null;
  } catch (error) {
    logger.error('Error getting FCM token:', error);
    return null;
  }
};

export const listenForegroundMessages = async (
  handler: (payload: MessagePayload) => void,
): Promise<void> => {
  try {
    const messaging = await ensureFirebaseMessaging();
    if (!messaging) return;
    onMessage(messaging, (payload) => {
      logger.log('FCM foreground message received:', payload);
      handler(payload);
    });
  } catch (error) {
    logger.error('Error registering FCM foreground listener:', error);
  }
};
